import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { OutsideLink } from '@clickcease/cc-client-components-lib'
import { ToasterType } from '@clickcease/cc-client-components-lib/dist/types/ToasterTypes'
import { ToasterProvider, ToasterViewport } from './ToasterMessages.styled'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { Toast } from 'Components/Elements/Toaster/Toast'
import { TOASTS } from 'Components/Elements/Toaster/Toast.models'
import { ToastType } from '../../types'
import { PLAN_OPTIONS, MIN_DOMAINS_LEFT } from '../../helpers'
import useAddDomainStore from '../../store/addDomainsStore'
import { useAppStore } from 'Stores/appStoreModuleFed'

export const ToasterMessage = () => {
  const { t } = useTranslation('domainManagement')
  const navigate = useNavigate()
  const { setModalToShow } = useAppStore()
  const { productsLeft, domainsLeft, allowedProducts } = useAddDomainStore()
  const { data: packagesData } = useGetPackagingLimits()
  const [toast, setToast] = useState<ToastType | undefined>(undefined)

  useEffect(
    () => handleToast(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productsLeft.botZapping, productsLeft.paidMarketing, domainsLeft]
  )

  const handleToast = () => {
    const { paidMarketing: paidMarketingLeft, botZapping: botZappingLeft } = productsLeft
    const { botZapping: bmAllowed, paidMarketing: pmAllowed } = allowedProducts
    const isBundle = pmAllowed && bmAllowed
    const pmAlmostAtLimit =
      pmAllowed && !!paidMarketingLeft && paidMarketingLeft <= MIN_DOMAINS_LEFT
    const bmAlmostAtLimit = bmAllowed && !!botZappingLeft && botZappingLeft <= MIN_DOMAINS_LEFT
    let content = 'add_new_multiple_domains_modal.toasters.'
    let type: ToasterType | undefined = undefined

    // Order of the toasts is important
    // 1. there are no domains left
    if (
      (bmAllowed && !botZappingLeft) ||
      (pmAllowed && !paidMarketingLeft) ||
      (!domainsLeft && domainsLeft !== null)
    ) {
      content += 'limit_reached.'
      type = TOASTS.failure

      if (!domainsLeft && isBundle) content += PLAN_OPTIONS.bundle
      else if (pmAllowed && !paidMarketingLeft) content += PLAN_OPTIONS.paidMarketing
      else if (bmAllowed && !botZappingLeft) content += PLAN_OPTIONS.botZapping
      else content = ''
    }
    // 2. there are less then 3 domains left
    else if (pmAlmostAtLimit || bmAlmostAtLimit) {
      content += 'limit_almost_reached.'
      type = TOASTS.warning

      if (pmAlmostAtLimit && bmAlmostAtLimit && isBundle) content += PLAN_OPTIONS.bundle
      else if (pmAlmostAtLimit) content += PLAN_OPTIONS.paidMarketing
      else if (bmAlmostAtLimit) content += PLAN_OPTIONS.botZapping
      else content = ''
    }

    if (type && content) {
      const bmDomainsAllowedNumber = bmAllowed ? packagesData?.botZappingDomainsLimit || 0 : 0
      const pmDomainsAllowedNumber = pmAllowed ? packagesData?.paidMarketingDomainsLimit || 0 : 0

      setToast({
        type,
        content: (
          <Trans
            t={t}
            i18nKey={content}
            values={{
              bmAllowed: bmDomainsAllowedNumber,
              pmAllowed: pmDomainsAllowedNumber,
              bmUsed: bmDomainsAllowedNumber - botZappingLeft,
              pmUsed: pmDomainsAllowedNumber - paidMarketingLeft,
            }}
            components={{
              br: <br />,
              upgrade: (
                <OutsideLink
                  onClick={() => {
                    setModalToShow('')
                    navigate('/modify-plan')
                  }}
                />
              ),
            }}
          />
        ),
      })
    } else setToast(undefined)
  }
  return (
    <ToasterProvider>
      <ToasterViewport>
        {toast?.type && (
          <Toast
            duration={Infinity}
            type={toast.type}
            onClose={() => setToast(undefined)}
            hideCloseButton={toast.type === TOASTS.failure}
          >
            {toast.content}
          </Toast>
        )}
      </ToasterViewport>
    </ToasterProvider>
  )
}
